import { Children, Fragment, isValidElement, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type SelectHTMLAttributes } from "react";
import { createPortal } from "react-dom";
import "./StyledSelect.css";

type Option = { value: string; label: string; disabled: boolean };
function optionsOf(children: ReactNode): Option[] {
  return Children.toArray(children).flatMap(child => {
    if (!isValidElement<{ value?: string; children?: ReactNode; disabled?: boolean }>(child)) return [];
    if (child.type === Fragment) return optionsOf(child.props.children);
    if (child.type !== "option") return [];
    const label = Children.toArray(child.props.children).join("");
    return [{ value: String(child.props.value ?? label), label, disabled: !!child.props.disabled }];
  });
}

const CHECK = <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4 10-10" /></svg>;

/**
 * A native form control with one accessible custom combobox, styled and behaving as the approved prototype's
 * dropdowns.js: the menu opens under the button (above it when there is no room), 200px wide at least and 280px tall
 * at most; arrows, Home / End and typeahead move, Enter / Space choose, Escape and Tab close; a press outside, a
 * resize or a scroll / wheel outside the menu closes it. Opening with an arrow moves one step at once. The native
 * select remains the source of change events, and a <label> (implicit or for=) activates the button.
 */
export function StyledSelect({ children, className = "", ...props }: SelectHTMLAttributes<HTMLSelectElement> & { "aria-label": string }) {
  const options = optionsOf(children);
  const native = useRef<HTMLSelectElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const id = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [position, setPosition] = useState<CSSProperties>({ position: "fixed", visibility: "hidden" });
  const typed = useRef({ value: "", at: 0 });
  const value = String(props.value ?? props.defaultValue ?? options[0]?.value ?? "");
  const selected = options.findIndex(o => o.value === value);
  const close = (focus = false) => { setOpen(false); if (focus) trigger.current?.focus({ preventScroll: true }); };
  const start = (step: 0 | 1 | -1 = 0) => {
    if (props.disabled || options.every(o => o.disabled)) return;
    const from = selected >= 0 && !options[selected]?.disabled ? selected : options.findIndex(o => !o.disabled);
    setActive(from);
    typed.current = { value: "", at: 0 };
    setOpen(true);
    if (step) move(from, step);
  };
  useLayoutEffect(() => {
    if (!open) return;
    const box = trigger.current?.getBoundingClientRect();
    if (!box) return;
    const space = window.innerHeight - box.bottom - 10;
    const width = Math.min(Math.max(box.width, 200), window.innerWidth - 16);
    setPosition({ position: "fixed", width, left: Math.max(8, Math.min(box.left, window.innerWidth - width - 8)),
      ...(space < 180 && box.top > space ? { bottom: window.innerHeight - box.top + 6, maxHeight: Math.min(280, box.top - 12) } : { top: box.bottom + 6, maxHeight: Math.max(90, Math.min(280, space)) }) });
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const outside = (e: Event) => { if (!trigger.current?.contains(e.target as Node) && !menu.current?.contains(e.target as Node)) close(); };
    const resize = () => close();
    const wheel = (e: Event) => { if (!menu.current?.contains(e.target as Node)) close(); };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("scroll", outside, true);
    window.addEventListener("resize", resize);
    window.addEventListener("wheel", wheel, { passive: true });
    window.addEventListener("touchmove", wheel, { passive: true });
    return () => {
      document.removeEventListener("pointerdown", outside); document.removeEventListener("scroll", outside, true); window.removeEventListener("resize", resize);
      window.removeEventListener("wheel", wheel); window.removeEventListener("touchmove", wheel);
    };
  }, [open]);
  useEffect(() => { if (props.disabled) close(); }, [props.disabled]);
  useEffect(() => {
    if (!open) return;
    const list = menu.current, option = list?.children[active] as HTMLElement | undefined;
    if (!list || !option) return;
    // Scroll only the options, never scrollIntoView: opening a portal must not move the page or its trigger.
    if (option.offsetTop < list.scrollTop) list.scrollTop = option.offsetTop;
    else if (option.offsetTop + option.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = option.offsetTop + option.offsetHeight - list.clientHeight;
  }, [active, open]);
  const choose = (index: number) => {
    const option = options[index];
    if (!option || option.disabled || !native.current) return;
    native.current.value = option.value;
    native.current.dispatchEvent(new Event("change", { bubbles: true }));
    close(true);
  };
  const move = (from: number, step: number) => {
    for (let n = 1; n <= options.length; n++) {
      const index = (from + step * n + options.length) % options.length;
      if (!options[index]?.disabled) { setActive(index); return; }
    }
  };
  return <span className={`styled-select ${className}`}>
    <button ref={trigger} id={props.id} type="button" role="combobox" aria-label={props["aria-label"]} aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? `${id}-list` : undefined}
      aria-activedescendant={open ? `${id}-${active}` : undefined} aria-describedby={props["aria-describedby"]} disabled={props.disabled}
      onClick={() => open ? close() : start()} onKeyDown={e => {
        if (e.key === "Tab") { close(); return; }
        if (e.key === "Escape" && open) { e.preventDefault(); e.stopPropagation(); close(true); return; }
        if (["Enter", " ", "ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) {
          e.preventDefault();
          if (!open) { start(e.key === "ArrowDown" ? 1 : e.key === "ArrowUp" ? -1 : 0); if (e.key === "Home") move(-1, 1); if (e.key === "End") move(0, -1); return; }
          if (e.key === "Enter" || e.key === " ") { choose(active); return; }
          if (e.key === "Home") move(-1, 1); else if (e.key === "End") move(0, -1); else move(active, e.key === "ArrowDown" ? 1 : -1);
        } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          e.preventDefault(); if (!open) start();
          const now = Date.now(); const text = `${now - typed.current.at < 700 ? typed.current.value : ""}${e.key.toLowerCase()}`;
          typed.current = { value: text, at: now };
          const index = options.findIndex(o => !o.disabled && o.label.toLowerCase().startsWith(text));
          if (index >= 0) setActive(index);
        }
      }}><span>{options[selected]?.label ?? options[0]?.label}</span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h.01 M12 12h.01 M19 12h.01" strokeWidth="3" strokeLinecap="round" /></svg></button>
    {/* The visible button owns label association; the native control only carries form values/events. */}
    <select {...props} id={undefined} ref={native} className="styled-select-native" tabIndex={-1} aria-label={undefined} aria-hidden="true">{children}</select>
    {open && createPortal(<div ref={menu} id={`${id}-list`} className="styled-options" role="listbox" aria-label={props["aria-label"]} style={position}>
      {options.map((o, i) => <div id={`${id}-${i}`} key={`${o.value}-${i}`} role="option" aria-selected={i === selected} aria-disabled={o.disabled || undefined}
        className={`styled-option${i === active ? " is-active" : ""}`} onPointerMove={() => { if (!o.disabled) setActive(i); }} onPointerDown={e => e.preventDefault()} onClick={() => choose(i)}>
        <span>{o.label}</span>{i === selected ? CHECK : null}
      </div>)}
    </div>, trigger.current?.closest("dialog") ?? document.body)}
  </span>;
}
